import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import {
  renderValidUrl,
  downloadPDF,
  getTokenFromCookie,
} from "../utils/constants";
import {
  isAuthenticated,
  getAuthHeaders,
  getAuthToken,
} from "../utils/authUtils";
import { ActionTypes } from "./actionTypes";

// Add auth headers for requests
export const authHeaders = {
  Authorization: `Bearer ${getAuthToken()}`,
  "Content-Type": "application/json",
};

export const setAppData = (hash) => (dispatch) => {
  dispatch({
    type: ActionTypes.SET_APP_DATA,
    hash: hash,
  });
};

export const showModalFunc =
  (authVariant = "student") =>
  (dispatch) => {
    dispatch({
      type: ActionTypes.SHOW_MODAL,
      showModal: true,
      authVariant,
    });
  };

export const showSignUpModal = () => (dispatch) => {
  dispatch({
    type: ActionTypes.SHOW_SIGNUP_MODAL,
    showSignUpModal: true,
  });
};

export const showMentorSignUpModal = () => (dispatch) => {
  dispatch({
    type: ActionTypes.SHOW_MENTOR_SIGNUP_MODAL,
    showMentorSignUpModal: true,
  });
};

export const hideModal = () => (dispatch) => {
  dispatch({
    type: ActionTypes.HIDE_MODAL,
    showModal: false,
    showSignUpModal: false,
    showMentorSignUpModal: false,
  });
};

export const login = (user) => {
  return {
    type: ActionTypes.LOGIN,
    payload: user,
  };
};

/**
 * UNIFIED ACCOUNT: Create a mentor profile for an already-authenticated user
 * who clicked "Become a Mentor" but has no mentor record yet.
 *
 * Reuses the existing backend register endpoint, which (for an existing user
 * matched by email) creates the mentor profile and returns a JWT + user.mentor.
 * On success we store the new token, attach the mentor to state.user, load the
 * full mentor record into state.mentor, and refresh the experiences state so
 * the rest of the app (dashboard CTA, route guards) reflects the new profile.
 */
export const becomeMentor = () => async (dispatch, getState) => {
  const state = getState();
  const user = state.user;
  if (!user) return [false, "Not authenticated"];
  // Already has a mentor profile, nothing to do
  if (user?.mentor?.id) return [true, null];

  const [ok, responseData] = await postRequest(
    "/api/auth/local/mentor/register",
    {
      email: user.email,
      firstname: user.firstname,
      lastname: user.lastname,
      // The backend requires a valid enum `country` to create the mentor
      // profile. Default to "Other" so registration succeeds; the mentor can
      // update their real country later via the profile Country form.
      country: "Other",
    },
  );

  // If a mentor profile already exists for this account, fetch it instead of
  // failing (can happen when experiences.mentor.hasProfile is true but the
  // local user.mentor wasn't hydrated).
  if (!ok || responseData?.error) {
    const alreadyExists = /already exists/i.test(
      responseData?.error?.message || "",
    );
    if (alreadyExists) {
      try {
        const token = getTokenFromCookie();
        const { data } = await axios.get("/users/me/mentor-profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (data?.id) {
          const mergedUser = { ...user, mentor: data, token: token || user.token };
          dispatch(login(mergedUser));
          dispatch(getMentorById(data.id));
          dispatch(fetchExperiences(token));
          return [true, data];
        }
      } catch (fetchErr) {
        console.error("Failed to fetch existing mentor profile:", fetchErr);
      }
    }
    const message =
      responseData?.error?.message ||
      "Failed to create your mentor profile. Please try again.";
    return [false, message];
  }

  const newJwt = responseData.jwt;
  const returnedUser = responseData.user || {};

  if (newJwt) {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 30);
    Cookies.set("token", newJwt, { expires: expirationDate });
  }

  const mergedUser = {
    ...user,
    ...returnedUser,
    originalRegisteredRole:
      user.originalRegisteredRole || returnedUser.originalRegisteredRole,
    token: newJwt || user.token,
  };
  dispatch(login(mergedUser));

  if (returnedUser.mentor?.id) {
    dispatch(getMentorById(returnedUser.mentor.id));
  }
  if (newJwt) {
    dispatch(fetchExperiences(newJwt));
  }

  return [true, responseData];
};

export const logout = () => {
  return {
    type: ActionTypes.LOGOUT,
  };
};

export const setFormData = (hash) => (dispatch) => {
  dispatch({
    type: ActionTypes.SET_FORM_DATA,
    hash,
  });
};

const fetchData = async (dispatch, url, type) => {
  dispatch({ type: ActionTypes.SET_IS_FETCHING, isFetching: true });

  try {
    const { data } = await axios.get(url);
    dispatch({ type: ActionTypes.GET_API_REQUEST, hash: { [type]: data } });
  } catch (error) {
    dispatch({ type: ActionTypes.SET_ERROR, error });
  }
};

export const getAnnouncement = () => async (dispatch) => {
  await fetchData(dispatch, "/announcements", "announcement");
};

export const getRegions = () => async (dispatch) => {
  await fetchData(dispatch, "/regions", "regions");
};

export const getTestimonials = () => async (dispatch) => {
  await fetchData(dispatch, "/testimonials", "testimonials");
};

export const getCourseSchedules = () => async (dispatch) => {
  await fetchData(dispatch, "/course-schedules", "courseSchedules");
};

export const getCourseBanners = () => async (dispatch) => {
  await fetchData(dispatch, "/course-banners", "courseBanners");
};

export const getBrochure = () => async (dispatch) => {
  await fetchData(dispatch, "/brochure", "brochure");
};
export const setUserEmail = (email) => ({
  type: ActionTypes.SET_USER_EMAIL,
  email,
});

export const resetUserEmail = () => ({
  type: ActionTypes.RESET_USER_EMAIL,
});

export const setUserType = (userType) => ({
  type: ActionTypes.SET_USER_TYPE,
  userType,
});

export const postRequest = (url, data, token = null) => {
  return fetch(renderValidUrl(url), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",

      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: JSON.stringify(data),
  })
    .then(async (response) => {
      const responseData = await response.json();
      // Return [false, responseData] for any HTTP error so callers can
      // detect 401 / 403 / 422 failures (previously always returned [true, …]).
      return [response.ok, responseData];
    })
    .catch((error) => {
      return [false, error];
    });
};

export const putRequest = async (url, data, token) => {
  try {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const response = await fetch(renderValidUrl(url), {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });

    const responseData = await response.json();
    return [true, responseData];
  } catch (error) {
    console.error("Error:", error);
    return [false, error];
  }
};

export const downloadPdfRequest = async (onClose) => {
  try {
    const { data } = await axios.get("/brochure");
    toast.success("PDF Successfully downloaded");
    downloadPDF(renderValidUrl(data.url));
    onClose();
  } catch (error) {
    toast.error("There was an error downloading the file");
  }
};

export const fetchThirdPartyRequest = async (url) => {
  try {
    const { data } = await axios.get(url);
    return data;
  } catch (error) {
    console.error("Error:", error);
  }
};

export const getSuccessStory = () => async (dispatch) => {
  // Fetch from testimonials API where mainToggle is true
  await fetchData(dispatch, "/testimonials", "successStories");
};

export const getCourseCarouselData = () => async (dispatch) => {
  await fetchData(dispatch, "/course-page-carousel", "courseCarouselData");
};

export const getCourses = () => async (dispatch) => {
  await fetchData(dispatch, "/courses", "courses");
};

export const getPrices = () => async (dispatch) => {
  await fetchData(dispatch, "/prices", "prices");
};

export const getCourseInstructors = () => async (dispatch) => {
  await fetchData(dispatch, "/course-instructors", "courseInstructors");
};

export const getPromotionData = () => async (dispatch) => {
  await fetchData(dispatch, "/promo-popup", "promotionData");
};

export const getBanner = () => async (dispatch) => {
  await fetchData(dispatch, "/banner", "bannerData");
};

export const getGoogleRatings = () => async (dispatch) => [
  await fetchData(dispatch, "/google-review", "googleRatings"),
];

export const updateUser = (user) => (dispatch) => {
  dispatch({
    type: ActionTypes.UPDATE_USER,
    payload: user,
  });
};

export const updateAvatar = (avatarUrl) => ({
  type: ActionTypes.UPDATE_AVATAR,
  payload: avatarUrl,
});

export const getUserProfile = (token) => async (dispatch, getState) => {
  dispatch({ type: ActionTypes.SET_IS_FETCHING, isFetching: true });
  try {
    // We populate enrolledCourses to ensure the dashboard can show correct enrollment status
    const response = await axios.get("/users/me?populate=*", {
      headers: {
        Authorization: `Bearer ${token}` || `Bearer ${getAuthToken()}`,
      },
    });

    // Preserve originalRegisteredRole from the current Redux state so the
    // backend response (which may lack the field for older users) cannot
    // overwrite the value we already resolved during login.
    const currentState = getState();
    const existingOriginalRole = currentState.user?.originalRegisteredRole;

    const profileData = response.data;
    if (existingOriginalRole && !profileData.originalRegisteredRole) {
      profileData.originalRegisteredRole = existingOriginalRole;
    }

    // Save the full user profile to Redux
    dispatch(updateUser(profileData));

    return [true, profileData];
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return [false, error];
  }
};

// UNIFIED ACCOUNT: Fetch student and mentor experience state
export const fetchExperiences = (token) => async (dispatch) => {
  dispatch({ type: ActionTypes.FETCH_EXPERIENCES_START });
  try {
    const authToken = token || getAuthToken();
    const { data } = await axios.get("/users/me/experiences", {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
    dispatch({ type: ActionTypes.FETCH_EXPERIENCES_SUCCESS, payload: data });
    return [true, data];
  } catch (error) {
    console.error("Error fetching experiences:", error);
    dispatch({ type: ActionTypes.FETCH_EXPERIENCES_ERROR, payload: error });
    return [false, error];
  }
};

// UNIFIED ACCOUNT: Activate student experience for current user (mentor -> student flow)
export const activateStudentExperience = () => async (dispatch, getState) => {
  try {
    const authToken = getAuthToken();
    if (!authToken) return [false, "Not authenticated"];

    const { data } = await axios.post(
      "/users/me/activate-student",
      {},
      { headers: { Authorization: `Bearer ${authToken}` } },
    );

    if (data?.success) {
      // Update user in Redux to reflect the change
      const state = getState();
      const user = state.user;
      if (user) {
        dispatch(login({ ...user, hasStudentExperience: true }));
      }
      // Refresh experiences to update sidebar CTAs
      dispatch(fetchExperiences(authToken));
      return [true, data];
    }
    return [false, data?.message || "Failed to activate student experience"];
  } catch (error) {
    console.error("Error activating student experience:", error);
    return [false, error?.response?.data?.error?.message || "Failed to activate student experience"];
  }
};

export const fetchCourseGroups = () => async (dispatch) => {
  dispatch({ type: ActionTypes.SET_IS_FETCHING, isFetching: true });
  try {
    const { data } = await axios.get("/course-pages");
    if (data?.data?.data && Array.isArray(data.data.data)) {
      data.data.data = data.data.data.filter((course) => course.hide !== true);
    }

    dispatch({
      type: ActionTypes.GET_API_REQUEST,
      hash: { courseGroups: data },
    });
  } catch (error) {
    dispatch({ type: ActionTypes.SET_ERROR, error });
  }
};

export const fetchCourseTypes = (courseId) => async (dispatch) => {
  await fetchData(dispatch, `/course-types?course=${courseId}&populate=deep`, "courseTypes");
};

export const fetchCourseFaqs = (courseId) => async (dispatch) => {
  await fetchData(dispatch, `/faqs?course=${courseId}`, "faqs");
};

export const fetchStartLearning = () => async (dispatch) => {
  await fetchData(dispatch, "/start-learnings", "startLearnings");
};

export const fetchCardsonly = (courseId) => async (dispatch) => {
  await fetchData(dispatch, `/cards-onlies?course=${courseId}`, "cardsOnly");
};

export const fetchDashboard = () => async (dispatch) => {
  dispatch({ type: ActionTypes.SET_IS_FETCHING, isFetching: true });
  try {
    const response = await axios.get("/dashboards");

    // Extract first dashboard item or use empty object as fallback
    const firstDashboard = (response.data.data && response.data.data[0]) || {};
    const attrs = firstDashboard.attributes || {};

    const dashboard = {
      speakToAdvisor: attrs.speakToAdvisor || "#",
      joinCommunityLink: attrs.joinCommunityLink || "#",
    };

    dispatch({ type: ActionTypes.GET_API_REQUEST, hash: { dashboard } });
  } catch (error) {
    console.error("Error fetching dashboard:", error);
    dispatch({ type: ActionTypes.SET_ERROR, error });
  }
};
export const getMentors = () => async (dispatch) => {
  dispatch({ type: ActionTypes.SET_IS_FETCHING, isFetching: true });
  try {
    const response = await axios.get("/mentors");

    // Use renderValidUrl for images
    const mentors = (response.data.data || []).map((item) => {
      const attrs = item.attributes || {};
      return {
        id: item.id,
        name: attrs.name,
        country: attrs.country, // new enumerated country field
        countryAlt: attrs.countryAlt,
        roleTitle: attrs.roleTitle,
        occupation: attrs.occupation,
        sessions: attrs.sessions,
        reviews: attrs.reviewCount,
        responseTime: attrs.responseTime,
        intro: attrs.intro,
        availability: attrs.availability,
        // Preserve null for missing hourlyRate; normalize numbers
        hourlyRate:
          attrs.hourlyRate === null ||
          attrs.hourlyRate === undefined ||
          attrs.hourlyRate === ""
            ? null
            : isNaN(parseFloat(attrs.hourlyRate))
              ? null
              : parseFloat(attrs.hourlyRate),
        location: attrs.location,
        email: attrs.email,
        rating: parseFloat(attrs.rating) || 0,
        // Use renderValidUrl for image; flag is now an emoji string
        image: renderValidUrl(attrs.image?.data?.attributes?.url),
        availableDates: attrs.availableDate || [],
        skills: (attrs.skill || []).map((s) => s.name),
        socialLinks: attrs.socialLinks || [],
        languages: (attrs.language || []).map((l) => l.language),
        certifications: (attrs.certification || []).map((c) => c.certification),
        education: attrs.education || [],
        workBackground: attrs.workBackground || [],
        reviewsData: (attrs.reviews?.data || []).map((r) => ({
          ...r.attributes,
          id: r.id,
        })),
        linkedIn: attrs.linkedInUrl,
        phoneNo: attrs.phoneNo,
        avatarUrl: attrs.avatarUrl,
        bio: attrs.bio,
        meetingLink: attrs.meetingLink,
        // New mentor roles fields
        mentorRoles: attrs.mentorRoles || [],
        maxYearsOfExperience: attrs.maxYearsOfExperience || 0,
        // Industry/domain field
        industry: attrs.industry || [],
      };
    });
    dispatch({ type: ActionTypes.GET_API_REQUEST, hash: { mentors } });
  } catch (error) {
    dispatch({ type: ActionTypes.SET_ERROR, error });
  }
};

export const getMentorById = (id) => async (dispatch) => {
  dispatch({ type: ActionTypes.SET_IS_FETCHING, isFetching: true });
  try {
    const response = await axios.get(`/mentors/${id}`);
    const data = response.data.data;
    let mentor = null;
    if (data && data.attributes) {
      const attrs = data.attributes;
      mentor = {
        id: data.id,
        name: attrs.name,
        country: attrs.country, // new enumerated country field
        countryAlt: attrs.countryAlt,
        roleTitle: attrs.roleTitle,
        occupation: attrs.occupation,
        industry: attrs.industry,
        sessions: attrs.sessions,
        reviews: attrs.reviewCount,
        responseTime: attrs.responseTime,
        intro: attrs.intro,
        availability: attrs.availability,
        // Preserve null when hourlyRate is not set; parse to number only if provided
        hourlyRate:
          attrs.hourlyRate === null ||
          attrs.hourlyRate === undefined ||
          attrs.hourlyRate === ""
            ? null
            : isNaN(parseFloat(attrs.hourlyRate))
              ? null
              : parseFloat(attrs.hourlyRate),
        // Add currency-specific rates for multi-currency mentor booking
        hourlyRateUSD: attrs.hourlyRateUSD || null,
        hourlyRateEUR: attrs.hourlyRateEUR || null,
        hourlyRateGBP: attrs.hourlyRateGBP || null,
        hourlyRateCAD: attrs.hourlyRateCAD || null,
        location: attrs.location,
        email: attrs.email,
        rating: parseFloat(attrs.rating) || 0,
        image: renderValidUrl(attrs.image?.data?.attributes?.url),
        availableDates: attrs.availableDate || [],
        skills: (attrs.skill || []).map((s) => ({
          id: s.id,
          name: s.name,
        })),
        socialLinks: attrs.socialLinks || [],
        languages: (attrs.language || []).map((l) => ({
          id: l.id,
          language: l.language,
        })),
        certifications: (attrs.certification || []).map((c) => ({
          id: c.id,
          certification: c.certification,
        })),
        education: attrs.education || [],
        workBackground: attrs.workBackground || [],
        reviewsData: (attrs.reviews?.data || []).map((r) => ({
          ...r.attributes,
          id: r.id,
        })),
        createdAt: attrs.createdAt,
        updatedAt: attrs.updatedAt,
        linkedIn: attrs.linkedInUrl,
        phoneNo: attrs.phoneNo,
        avatarUrl: attrs.avatarUrl,
        bio: attrs.bio,
        meetingLink: attrs.meetingLink,
        // Profile submission workflow fields
        profileStatus: attrs.profileStatus || "draft",
        submittedAt: attrs.submittedAt,
        approvedAt: attrs.approvedAt,
        rejectedAt: attrs.rejectedAt,
        rejectionReason: attrs.rejectionReason,
        publishedAt: attrs.publishedAt,
        // New mentor roles fields
        mentorRoles: attrs.mentorRoles || [],
        maxYearsOfExperience: attrs.maxYearsOfExperience || 0,
        // Industry/domain field
      };
    }
    dispatch({ type: ActionTypes.GET_SINGLE_MENTOR_SUCCESS, payload: mentor });
    return mentor;
  } catch (error) {
    dispatch({ type: ActionTypes.GET_SINGLE_MENTOR_ERROR, error });
    return null;
  }
};

// Fetch mentor's upcoming sessions
export const fetchUpcomingSessions = () => async (dispatch) => {
  dispatch({ type: ActionTypes.SET_IS_FETCHING, isFetching: true });

  try {
    // Try multiple token sources to ensure compatibility
    const token =
      getAuthToken() ||
      getTokenFromCookie() ||
      localStorage.getItem("authToken");
    if (!token) {
      dispatch({
        type: ActionTypes.FETCH_UPCOMING_SESSIONS_ERROR,
        error: "Authentication token is missing. Please log in again.",
      });
      return [];
    }

    // Use correct API URL format to avoid path issues
    const BACKEND_URL =
      process.env.REACT_APP_API_URL.replace("/api", "") ||
      "http://localhost:1337";

    // Use mentor endpoint for upcoming sessions
    const url = `${BACKEND_URL}/api/mentors/my-upcoming-sessions`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    // Handle unauthorized response
    if (response.status === 401) {
      dispatch({
        type: ActionTypes.FETCH_UPCOMING_SESSIONS_ERROR,
        error: "Your session has expired. Please log in again.",
      });
      return [];
    }

    const data = await response.json();

    // Handle both possible response structures (sessions or data)
    const sessions = data.sessions || data.data || [];

    if (data.success !== false && Array.isArray(sessions)) {
      dispatch({
        type: ActionTypes.FETCH_UPCOMING_SESSIONS_SUCCESS,
        payload: {
          mentorId: "current",
          bookings: sessions,
        },
      });
      return sessions;
    } else {
      dispatch({
        type: ActionTypes.FETCH_UPCOMING_SESSIONS_ERROR,
        error: data.message || "Failed to fetch upcoming sessions",
      });
      return [];
    }
  } catch (error) {
    console.error("Error fetching upcoming sessions:", error);
    dispatch({
      type: ActionTypes.FETCH_UPCOMING_SESSIONS_ERROR,
      error: error.message || "Failed to fetch upcoming sessions",
    });
    return [];
  }
};

/**
 * Submit a review action
 * @param {Object} reviewData - The review data to submit
 * @returns {Function} Thunk action function
 */
export const submitReview = (reviewData) => async (dispatch) => {
  if (!isAuthenticated()) {
    toast.error("You must be logged in to submit a review");
    return Promise.reject(
      new Error("You must be logged in to submit a review"),
    );
  }

  dispatch({ type: ActionTypes.SUBMIT_REVIEW_START });

  try {
    const API_URL =
      process.env.REACT_APP_API_URL || "http://localhost:1337/api";
    const response = await axios.post(
      `${API_URL}/reviews`,
      { data: reviewData },
      { headers: getAuthHeaders() },
    );

    dispatch({
      type: ActionTypes.SUBMIT_REVIEW_SUCCESS,
      payload: response.data,
    });

    toast.success("Review submitted successfully!");
    return response.data;
  } catch (error) {
    const errorMessage =
      error.response?.status === 401
        ? "Authentication required to submit a review. Please log in and try again."
        : error.message || "Failed to submit review. Please try again.";

    dispatch({
      type: ActionTypes.SUBMIT_REVIEW_ERROR,
      payload: errorMessage,
    });

    toast.error(errorMessage);
    return Promise.reject(new Error(errorMessage));
  }
};

export const fetchCourseReviews = (courseId) => async (dispatch) => {
  dispatch({ type: ActionTypes.FETCH_COURSE_REVIEWS_START });
  try {
    const API_URL =
      process.env.REACT_APP_API_URL || "http://localhost:1337/api";
    // Usually it filters by course relation. E.g. course: courseId or course_page: courseId
    const response = await axios.get(`${API_URL}/reviews/course/${courseId}`);

    // Depending on the exact schema, it might be nested in data.data or similar
    const reviews = response.data?.data || [];

    dispatch({
      type: ActionTypes.FETCH_COURSE_REVIEWS_SUCCESS,
      payload: reviews,
    });
    return reviews;
  } catch (error) {
    const errorMessage =
      error.response?.data?.error?.message ||
      error.message ||
      "Failed to fetch course reviews";
    dispatch({
      type: ActionTypes.FETCH_COURSE_REVIEWS_ERROR,
      payload: errorMessage,
    });
    return [];
  }
};

export const submitCourseReview = (reviewData) => async (dispatch) => {
  if (!isAuthenticated()) {
    toast.error("You must be logged in to submit a review");
    return Promise.reject(
      new Error("You must be logged in to submit a review"),
    );
  }

  dispatch({ type: ActionTypes.SUBMIT_COURSE_REVIEW_START });

  try {
    const API_URL =
      process.env.REACT_APP_API_URL || "http://localhost:1337/api";
    const response = await axios.post(
      `${API_URL}/reviews`,
      { data: reviewData },
      { headers: getAuthHeaders() },
    );

    dispatch({
      type: ActionTypes.SUBMIT_COURSE_REVIEW_SUCCESS,
      payload: response.data,
    });

    toast.success("Review submitted successfully!");
    return response.data;
  } catch (error) {
    const errorMessage =
      error.response?.status === 401
        ? "Authentication required to submit a review. Please log in and try again."
        : error.message || "Failed to submit review. Please try again.";

    dispatch({
      type: ActionTypes.SUBMIT_COURSE_REVIEW_ERROR,
      payload: errorMessage,
    });

    toast.error(errorMessage);
    return Promise.reject(new Error(errorMessage));
  }
};

// Fetch past sessions for the currently logged-in mentor
export const fetchPastSessions = () => async (dispatch) => {
  dispatch({ type: ActionTypes.SET_IS_FETCHING, isFetching: true });

  try {
    // Try multiple token sources to ensure compatibility
    const token =
      getAuthToken() ||
      getTokenFromCookie() ||
      localStorage.getItem("authToken");
    if (!token) {
      dispatch({
        type: ActionTypes.FETCH_PAST_SESSIONS_ERROR,
        error: "Authentication token is missing. Please log in again.",
      });
      return [];
    }

    const BACKEND_URL =
      process.env.REACT_APP_API_URL.replace("/api", "") ||
      "http://localhost:1337";

    // Use the correct mentor endpoint for past sessions
    const url = `${BACKEND_URL}/api/mentors/my-past-sessions`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (response.status === 401) {
      dispatch({
        type: ActionTypes.FETCH_PAST_SESSIONS_ERROR,
        error: "Your session has expired. Please log in again.",
      });
      return [];
    }

    const data = await response.json();

    // Handle both possible response structures (sessions or data)
    const sessions = data.sessions || data.data || [];

    if (data.success !== false && Array.isArray(sessions)) {
      dispatch({
        type: ActionTypes.FETCH_PAST_SESSIONS_SUCCESS,
        payload: sessions,
      });
      return sessions;
    } else {
      dispatch({
        type: ActionTypes.FETCH_PAST_SESSIONS_ERROR,
        error: data.message || "Failed to fetch past sessions",
      });
      return [];
    }
  } catch (error) {
    console.error("Error fetching past sessions:", error);
    dispatch({
      type: ActionTypes.FETCH_PAST_SESSIONS_ERROR,
      error: error.message || "Failed to fetch past sessions",
    });
    return [];
  } finally {
    dispatch({ type: ActionTypes.SET_IS_FETCHING, isFetching: false });
  }
};

/**
 * Action to set a reminder for an event.
 * @param {string} eventId - The ID of the event.
 * @param {string} occurrenceDate - The date of the event.
 * @param {string} userId - The ID of the user.
 * @returns {Function} Thunk action function.
 */
export const setEventReminder =
  (eventId, occurrenceDate, userId) => async (dispatch) => {
    dispatch({ type: ActionTypes.SET_EVENT_REMINDER_START });

    try {
      const API_URL =
        process.env.REACT_APP_API_URL || "http://localhost:1337/api";
      const token = getAuthToken();

      await axios.post(
        `${API_URL}/event-reminders`,
        {
          data: {
            calendar_event: eventId,
            occurrenceDate: occurrenceDate,
            user: userId,
          },
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      dispatch({ type: ActionTypes.SET_EVENT_REMINDER_SUCCESS });
      toast.success("Reminder added for this event!");
      return true;
    } catch (error) {
      console.error("Failed to set reminder:", error);
      dispatch({
        type: ActionTypes.SET_EVENT_REMINDER_ERROR,
        payload: error.message || "Failed to set reminder",
      });
      toast.error("Failed to set reminder. Please try again.");
      return false;
    }
  };

// Fetch referrals
export const fetchReferrals = () => async (dispatch, getState) => {
  try {
    const token = getAuthToken();
    const state = getState();
    const userId =
      state.user?.id || state.user?.user?.id || state.auth?.user?.id;

    let url = "/form-referrals?populate=*";
    if (userId) {
      url += `&filters[referrer][id][$eq]=${userId}`;
    }

    const config = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    const { data } = await axios.get(url, config);

    const fetchedReferrals =
      data?.data?.map((item) => {
        const attr = item.attributes || item;
        const courseData = attr.course?.data?.attributes || attr.course;

        return {
          id: item.id,
          name: `${attr.firstname || ""} ${attr.lastname || ""}`.trim(),
          email: attr.email,
          program: attr.program,
          course: courseData?.title || courseData?.name || attr.course || "N/A",
          reward: attr.reward || "N/A",
          status: "Invite Sent",
          action: "Nudge",
        };
      }) || [];

    dispatch({
      type: ActionTypes.FETCH_REFERRALS_SUCCESS,
      payload: fetchedReferrals,
    });
  } catch (error) {
    console.error("Error fetching referrals:", error);
  }
};
